/**
 * Codificación de tipos de factura permitidos para `Cliente.tipoFacturaPermitida`.
 *
 * El backend persiste un único string (CharField ~10) vía CamelCaseJSONParser
 * como `tipoFacturaPermitida` ↔ `tipo_factura_permitida`.
 *
 * Bug histórico: el ABM enviaba solo `array[0]`, de modo que con orden ["X","A"]
 * se guardaba "X" y se perdía A. Aquí se codifica de forma determinística.
 */
export type TipoFacturaFlag = "A" | "B" | "X";

export function encodeTipoFacturaPermitida(selected: TipoFacturaFlag[]): string {
  const hasA = selected.includes("A");
  const hasB = selected.includes("B");
  const hasX = selected.includes("X");
  if (hasA) {
    return hasX ? "A+X" : "A";
  }
  if (hasB) {
    return hasX ? "B+X" : "B";
  }
  return "X";
}

export function decodeTipoFacturaPermitida(
  stored: string | undefined | null
): TipoFacturaFlag[] {
  const v = (stored ?? "B").trim().toUpperCase();
  if (v === "A+X") return ["A", "X"];
  if (v === "B+X") return ["B", "X"];
  if (v === "A") return ["A", "X"];
  if (v === "B") return ["B", "X"];
  if (v === "X") return ["X"];
  return ["B", "X"];
}

/**
 * Carga ABM: siempre refleja el string persistido; si llegara un array legacy,
 * lo canoniza vía encode→decode para no perder A/B por orden.
 */
export function normalizeStoredTiposPermitidosForForm(
  raw: string | TipoFacturaFlag[] | undefined | null
): TipoFacturaFlag[] {
  if (Array.isArray(raw)) {
    const flags = raw.filter((x): x is TipoFacturaFlag => x === "A" || x === "B" || x === "X");
    if (flags.length === 0) return decodeTipoFacturaPermitida("B");
    return decodeTipoFacturaPermitida(encodeTipoFacturaPermitida(flags));
  }
  return decodeTipoFacturaPermitida(raw ?? undefined);
}

/** Para UI que asume array (FacturacionList, etc.) */
export function normalizeClienteTiposPermitidos(raw: unknown): TipoFacturaFlag[] {
  if (Array.isArray(raw)) {
    return raw.filter((x): x is TipoFacturaFlag => x === "A" || x === "B" || x === "X");
  }
  if (raw === undefined || raw === null) {
    return ["B", "X"];
  }
  return decodeTipoFacturaPermitida(String(raw));
}

export function clientePermiteCae(tipos: TipoFacturaFlag[]): boolean {
  return tipos.includes("A") || tipos.includes("B");
}

/**
 * Display-only: DNI con puntos argentinos (ej. 22222222 → 22.222.222).
 * No altera lo persistido en DB. Si no hay dígitos o supera 8 (p.ej. CUIT),
 * devuelve el valor original trimmeado.
 */
export function formatDniDisplay(raw: string | null | undefined): string {
  const s = (raw ?? "").trim();
  if (!s || s === "-") return s || "-";
  const digits = s.replace(/\D/g, "");
  if (!digits || digits.length > 8) return s;
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/**
 * Documento “activo” alineado con ClientesABM:
 * - Factura A (con o sin X) → CUIT
 * - Factura B sin A (B / B+X) → CUIT o DNI (el que esté cargado)
 * - Solo X → CUIT (sin DNI activo en el ABM)
 *
 * `value` es para UI: DNI ya viene con formatDniDisplay; CUIT sin cambiar.
 */
export type ClienteDocumentoActivo = {
  label: "CUIT" | "DNI";
  value: string;
  usesDni: boolean;
};

export const CONDICION_IVA_RI = "Responsable Inscripto";
export const CONDICION_IVA_CF = "Consumidor Final";
export const CONDICION_IVA_EXENTO_B = "IVA Exento";

/** Texto persistido en `cliente.condicion_iva` → pdfme `cliente_condicion_iva`. Sin Monotributo/Exento. */
export const PDF_CONDICION_IVA_OPCIONES: { value: string; label: string }[] = [
  { value: CONDICION_IVA_RI, label: "Responsable Inscripto (Fact. A habitual)" },
  { value: CONDICION_IVA_CF, label: "Consumidor Final (Fact. B habitual)" },
  { value: CONDICION_IVA_EXENTO_B, label: "IVA Exento (Fact. B)" },
];

export function condicionIvaOpcionesParaTipos(
  tipos: TipoFacturaFlag[]
): { value: string; label: string }[] {
  if (tipos.includes("A")) {
    return PDF_CONDICION_IVA_OPCIONES.filter((o) => o.value === CONDICION_IVA_RI);
  }
  return PDF_CONDICION_IVA_OPCIONES.filter(
    (o) => o.value === CONDICION_IVA_CF || o.value === CONDICION_IVA_EXENTO_B
  );
}

export function coerceCondicionIvaForTipos(
  tipos: TipoFacturaFlag[],
  current: string
): string {
  if (tipos.includes("A")) return CONDICION_IVA_RI;
  const allowed = new Set(condicionIvaOpcionesParaTipos(tipos).map((o) => o.value));
  if (allowed.has(current)) return current;
  return CONDICION_IVA_CF;
}

export function getClienteDocumentoActivo(cliente: {
  cuit?: string | null;
  dni?: string | null;
  tipoFacturaPermitida?: string | TipoFacturaFlag[] | null;
}): ClienteDocumentoActivo {
  const tipos = normalizeClienteTiposPermitidos(cliente.tipoFacturaPermitida);
  const hasA = tipos.includes("A");
  const hasB = tipos.includes("B");
  const cuit = (cliente.cuit || "").trim();
  const dni = (cliente.dni || "").trim();
  if (hasB && !hasA) {
    if (cuit && !dni) {
      return { label: "CUIT", value: cuit, usesDni: false };
    }
    if (dni) {
      return {
        label: "DNI",
        value: formatDniDisplay(dni),
        usesDni: true,
      };
    }
    if (cuit) {
      return { label: "CUIT", value: cuit, usesDni: false };
    }
    return { label: "DNI", value: "-", usesDni: true };
  }
  return { label: "CUIT", value: cuit || "-", usesDni: false };
}
